// The public status page at /status (TOM-930, cloud ADR 0453). In order: the
// banner with any open incident, upcoming maintenance, the signed-in owner's
// boats, the four components with 90 days each, and the incident calendar.
//
// It reads only /v1/status* and depends on nothing inside MarketingLayout, so
// it can move to tombolostatus.com as it is.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth, useDemoGuest } from '../auth';
import { UptimeBars, type UptimeBarDay } from '../components/charts';
import { boatPresenceLabel, getBoatPresence } from '../lib/boatPresence';
import { formatRelative } from '../lib/deviceSeen';
import {
  COMPONENT_STATE_LABELS,
  IMPACT_LABELS,
  INCIDENT_STATE_LABELS,
  NARROW_BARS_PX,
  NARROW_DAYS,
  boatConnectedLine,
  boatDayBar,
  calendarWeeks,
  componentDayBar,
  componentTone,
  formatDay,
  formatInstant,
  formatMinutes,
  formatMonth,
  formatPercent,
  incidentTouchesDate,
  lastDays,
  monthKey,
  overallTone,
  shiftMonth,
  windowUptime,
  type BoatLink,
  type Incident,
  type StatusComponent,
  type StatusPage,
} from '../lib/statusPage';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useMediaQuery } from '../lib/useMediaQuery';
import { useIncidentMonth, useStatusBoats, useStatusPage } from '../lib/useStatusPage';

const FULL_DAYS = 90;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function Status() {
  useDocumentTitle('Neomaris | Status');
  const page = useStatusPage();
  const narrow = useMediaQuery(`(max-width: ${NARROW_BARS_PX - 1}px)`);
  const shown = narrow ? NARROW_DAYS : FULL_DAYS;

  return (
    <section className="status-page" aria-labelledby="status-title">
      <header className="status-head">
        <h1 id="status-title">Neomaris status</h1>
        <p>Whether Neomaris&apos;s own services are working, checked every minute.</p>
      </header>

      {page.kind === 'loading' && <p className="status-note">Loading status…</p>}
      {page.kind === 'error' && (
        <p className="status-note" role="alert">
          We couldn&apos;t load the status just now. This page tries again every minute.
        </p>
      )}
      {page.kind === 'ready' && (
        <>
          <StatusBanner page={page.data} />
          {page.data.upcomingMaintenance.length > 0 && (
            <UpcomingMaintenance incidents={page.data.upcomingMaintenance} />
          )}
          <YourBoatsSection shown={shown} />
          <SystemStatus page={page.data} shown={shown} />
          <IncidentCalendar page={page.data} />
        </>
      )}
    </section>
  );
}

function IncidentSummary({ incident }: { incident: Incident }) {
  return (
    <li className="status-incident">
      <div className="status-incident__head">
        <Link className="status-incident__title" to={`/status/incidents/${incident.id}`}>
          {incident.title}
        </Link>
        <span className={`tb-chip tb-chip--${incident.impact === 'major' ? 'bad' : 'warn'}`}>
          {incident.kind === 'maintenance'
            ? INCIDENT_STATE_LABELS[incident.state]
            : `${IMPACT_LABELS[incident.impact]} · ${INCIDENT_STATE_LABELS[incident.state]}`}
        </span>
      </div>
      {incident.latestUpdate !== null && (
        <p className="status-incident__body">
          {incident.latestUpdate.body}{' '}
          <span className="status-muted">{formatInstant(incident.latestUpdate.createdAt)}</span>
        </p>
      )}
    </li>
  );
}

function StatusBanner({ page }: { page: StatusPage }) {
  const tone = overallTone(page.overall.state);
  return (
    <div className="tb-card status-banner" data-tone={tone} role="status">
      <div className="status-banner__head">
        <span className={`status-banner__mark status-banner__mark--${tone}`} aria-hidden="true" />
        <h2 className="status-banner__label">{page.overall.label}</h2>
        <span className="status-muted status-banner__time">
          Updated {formatInstant(page.generatedAt)}
        </span>
      </div>
      {page.activeIncidents.length > 0 && (
        <ul className="status-incidents">
          {page.activeIncidents.map((i) => (
            <IncidentSummary key={i.id} incident={i} />
          ))}
        </ul>
      )}
    </div>
  );
}

function UpcomingMaintenance({ incidents }: { incidents: Incident[] }) {
  return (
    <section className="status-section" aria-labelledby="status-maintenance">
      <h2 id="status-maintenance">Upcoming maintenance</h2>
      <ul className="tb-card status-incidents">
        {incidents.map((i) => (
          <li key={i.id} className="status-incident">
            <div className="status-incident__head">
              <Link className="status-incident__title" to={`/status/incidents/${i.id}`}>
                {i.title}
              </Link>
              <span className="tb-chip">Scheduled</span>
            </div>
            <p className="status-incident__body">
              {formatInstant(i.startsAt)}
              {i.endsAt !== null ? ` to ${formatInstant(i.endsAt)}` : ''}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function YourBoatsSection({ shown }: { shown: number }) {
  const auth = useAuth();
  const guest = useDemoGuest();
  // A Try it guest's demo boat has no connection history (TOM-930).
  if (guest) return null;
  return (
    <section className="status-section" aria-labelledby="status-boats">
      <h2 id="status-boats">Your boats</h2>
      {auth.isAuthenticated ? (
        <YourBoats shown={shown} />
      ) : (
        <p className="tb-card status-signin">
          <Link className="link" to="/login" state={{ from: '/status' }}>
            Sign in to see your boat&apos;s connection
          </Link>
        </p>
      )}
    </section>
  );
}

/** Mounted only for a signed-in user, so an anonymous visitor never asks for boats. */
function YourBoats({ shown }: { shown: number }) {
  const state = useStatusBoats();
  if (state.kind === 'loading') return <p className="status-note">Loading your boats…</p>;
  if (state.kind === 'error') {
    return <p className="status-note">We couldn&apos;t load your boats just now.</p>;
  }
  if (state.data.boats.length === 0) {
    return <p className="tb-card status-signin">No boats on your account yet.</p>;
  }
  return (
    <div className="tb-card status-rows">
      {state.data.boats.map((b) => (
        <BoatRow key={b.id} boat={b} shown={shown} />
      ))}
    </div>
  );
}

function BoatRow({ boat, shown }: { boat: BoatLink; shown: number }) {
  const now = Date.now();
  const presence = getBoatPresence(boat.lastContactedAt, boat.createdAt, undefined, now);
  const days = lastDays(boat.days, shown);
  return (
    <div className="status-row" data-testid="status-boat">
      <div className="status-row__head">
        <span className="status-row__name">
          <span className={`boat-status-dot boat-status-dot--${presence}`} aria-hidden="true" />
          {boat.name}
        </span>
        <span className="status-row__state" data-presence={presence}>
          {boatPresenceLabel(presence)}
        </span>
      </div>
      <UptimeBars days={days.map(boatDayBar)} ariaLabel={`${boat.name}, last ${shown} days`} />
      <div className="status-row__foot">
        <span>{boatConnectedLine(days)}</span>
        <span className="status-muted">
          Last contact {formatRelative(boat.lastContactedAt, now).toLowerCase()}
        </span>
      </div>
    </div>
  );
}

function SystemStatus({ page, shown }: { page: StatusPage; shown: number }) {
  const titles = useMemo(
    () =>
      new Map([...page.incidents, ...page.activeIncidents].map((i) => [i.id, i.title] as const)),
    [page]
  );
  return (
    <section className="status-section" aria-labelledby="status-system">
      <h2 id="status-system">System status</h2>
      <div className="tb-card status-rows">
        {page.components.map((c) => (
          <ComponentRow key={c.id} component={c} shown={shown} titles={titles} />
        ))}
      </div>
      <p className="status-muted status-legend">
        Each bar is one day, in UTC. A minute with no check counts as down.
        {page.monitoringSince !== null &&
          ` Checks began ${formatDay(page.monitoringSince.slice(0, 10))}.`}
      </p>
    </section>
  );
}

function ComponentRow({
  component,
  shown,
  titles,
}: {
  component: StatusComponent;
  shown: number;
  titles: ReadonlyMap<string, string>;
}) {
  const days = lastDays(component.days, shown);
  const uptime = windowUptime(days);
  const descId = `status-desc-${component.id}`;
  const bars: UptimeBarDay[] = days.map((d) => ({
    ...componentDayBar(d),
    detail:
      d.expectedMinutes === 0 && d.incidentIds.length === 0 ? undefined : (
        <>
          {d.expectedMinutes > 0 && (
            <div>
              {formatPercent(windowUptime([d]))} uptime
              {d.downMinutes > 0 && ` · ${formatMinutes(d.downMinutes)} down`}
              {d.degradedMinutes > 0 && ` · ${formatMinutes(d.degradedMinutes)} degraded`}
            </div>
          )}
          {d.incidentIds.map((id) => (
            <div key={id}>
              <Link to={`/status/incidents/${id}`}>{titles.get(id) ?? 'Incident'}</Link>
            </div>
          ))}
        </>
      ),
  }));
  return (
    <div className="status-row" data-testid={`status-component-${component.id}`}>
      <div className="status-row__head">
        <span className="status-row__name">
          {component.name}
          <span
            className="status-info"
            tabIndex={0}
            role="img"
            aria-label={`About ${component.name}`}
            aria-describedby={descId}
            title={component.description}
          >
            i
          </span>
          <span id={descId} className="sr-only">
            {component.description}
          </span>
        </span>
        <span className={`tb-status tb-status--${componentTone(component.state)}`}>
          {COMPONENT_STATE_LABELS[component.state]}
        </span>
      </div>
      <UptimeBars days={bars} ariaLabel={`${component.name}, last ${shown} days`} />
      <div className="status-row__foot status-muted">
        <span>{shown} days ago</span>
        <span>{uptime === null ? 'No data yet' : `${formatPercent(uptime)} uptime`}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

function IncidentCalendar({ page }: { page: StatusPage }) {
  const now = Date.parse(page.generatedAt);
  const today = page.generatedAt.slice(0, 10);
  const [thisYear, thisMonth] = [Number(today.slice(0, 4)), Number(today.slice(5, 7))];
  const [[year, month], setMonth] = useState<[number, number]>([thisYear, thisMonth]);
  const [selected, setSelected] = useState<string | null>(null);
  const isCurrent = year === thisYear && month === thisMonth;
  // The current month is in the page already; earlier ones are fetched.
  const fetched = useIncidentMonth(isCurrent ? null : monthKey(year, month));
  const monthStart = Date.UTC(year, month - 1, 1);
  const incidents = isCurrent
    ? dedupe([...page.activeIncidents, ...page.incidents]).filter(
        (i) => i.endsAt === null || Date.parse(i.endsAt) >= monthStart
      )
    : fetched.kind === 'ready'
      ? fetched.data.incidents
      : [];
  const weeks = calendarWeeks(year, month);
  const onDay = (date: string) => incidents.filter((i) => incidentTouchesDate(i, date, now));
  const list = selected === null ? incidents : onDay(selected);

  const go = (by: number) => {
    setMonth(shiftMonth(year, month, by));
    setSelected(null);
  };

  return (
    <section className="status-section" aria-labelledby="status-calendar">
      <h2 id="status-calendar">Incidents</h2>
      <div className="tb-card status-calendar">
        <div className="status-calendar__nav">
          <button type="button" className="status-calendar__step" onClick={() => go(-1)}>
            <span aria-hidden="true">‹</span> <span className="sr-only">Previous month</span>
          </button>
          <h3 aria-live="polite">{formatMonth(year, month)}</h3>
          <button
            type="button"
            className="status-calendar__step"
            onClick={() => go(1)}
            disabled={isCurrent}
          >
            <span aria-hidden="true">›</span> <span className="sr-only">Next month</span>
          </button>
        </div>
        <table className="status-calendar__grid">
          <thead>
            <tr>
              {WEEKDAYS.map((d) => (
                <th key={d} scope="col">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week[0]!.date}>
                {week.map((cell) => {
                  const these = cell.inMonth ? onDay(cell.date) : [];
                  const worst = these.some((i) => i.impact === 'major')
                    ? 'bad'
                    : these.some((i) => i.impact === 'minor')
                      ? 'warn'
                      : these.length > 0
                        ? 'muted'
                        : null;
                  return (
                    <td key={cell.date}>
                      {cell.inMonth ? (
                        <button
                          type="button"
                          className="status-calendar__day"
                          data-selected={selected === cell.date ? '' : undefined}
                          data-today={cell.date === today ? '' : undefined}
                          aria-pressed={selected === cell.date}
                          aria-label={`${formatDay(cell.date)}: ${
                            these.length === 0
                              ? 'no incidents'
                              : `${these.length} incident${these.length === 1 ? '' : 's'}`
                          }`}
                          onClick={() => setSelected(selected === cell.date ? null : cell.date)}
                        >
                          <span>{cell.day}</span>
                          {worst !== null && (
                            <span
                              className={`status-calendar__mark status-calendar__mark--${worst}`}
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="status-calendar__list">
          <h4>{selected === null ? formatMonth(year, month) : formatDay(selected)}</h4>
          {!isCurrent && fetched.kind === 'loading' ? (
            <p className="status-muted">Loading…</p>
          ) : !isCurrent && fetched.kind === 'error' ? (
            <p className="status-muted">We couldn&apos;t load this month.</p>
          ) : list.length === 0 ? (
            <p className="status-muted">
              {selected === null ? 'No incidents this month.' : 'No incidents on this day.'}
            </p>
          ) : (
            <ul className="status-incidents">
              {list.map((i) => (
                <IncidentSummary key={i.id} incident={i} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function dedupe(incidents: Incident[]): Incident[] {
  const seen = new Set<string>();
  return incidents.filter((i) => (seen.has(i.id) ? false : (seen.add(i.id), true)));
}
